import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { COGNITO_CLIENT_ID, COGNITO_CONFIGURED, COGNITO_USER_POOL_ID } from '../config/cognito';
import { setAuthTokenGetter } from '../lib/api';

interface AuthUser {
  email: string;
}

export type LoginResult =
  | { status: 'success' }
  | { status: 'newPasswordRequired' };

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const userPool = COGNITO_CONFIGURED
  ? new CognitoUserPool({ UserPoolId: COGNITO_USER_POOL_ID, ClientId: COGNITO_CLIENT_ID })
  : null;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const idTokenRef = useRef<string | undefined>(undefined);
  // Usuário no meio do desafio NEW_PASSWORD_REQUIRED (primeiro acesso).
  const pendingUserRef = useRef<CognitoUser | null>(null);
  const pendingAttrsRef = useRef<Record<string, string> | null>(null);

  // Registra o provedor de token para o cliente HTTP injetar Authorization.
  useEffect(() => {
    setAuthTokenGetter(() => idTokenRef.current);
  }, []);

  const applySession = useCallback((cognitoUser: CognitoUser, session: CognitoUserSession) => {
    idTokenRef.current = session.getIdToken().getJwtToken();
    const email = session.getIdToken().payload.email ?? cognitoUser.getUsername();
    setUser({ email });
  }, []);

  const clearSession = useCallback(() => {
    idTokenRef.current = undefined;
    pendingUserRef.current = null;
    pendingAttrsRef.current = null;
    setUser(null);
  }, []);

  // Restaura sessão existente no mount.
  useEffect(() => {
    if (!userPool) {
      setLoading(false);
      return;
    }
    const current = userPool.getCurrentUser();
    if (!current) {
      setLoading(false);
      return;
    }
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (!err && session?.isValid()) {
        applySession(current, session);
      } else {
        idTokenRef.current = undefined;
      }
      setLoading(false);
    });
  }, [applySession]);

  const logout = useCallback(() => {
    const current = userPool?.getCurrentUser();
    current?.signOut();
    clearSession();
  }, [clearSession]);

  const login = useCallback(
    (email: string, password: string): Promise<LoginResult> => {
      if (!userPool) {
        return Promise.reject(new Error('Autenticação não configurada.'));
      }
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      return new Promise<LoginResult>((resolve, reject) => {
        cognitoUser.authenticateUser(authDetails, {
          onSuccess: (session) => {
            applySession(cognitoUser, session);
            resolve({ status: 'success' });
          },
          onFailure: (err) => reject(err),
          newPasswordRequired: (userAttributes) => {
            // Atributos imutáveis não podem ser reenviados no desafio.
            delete userAttributes.email_verified;
            delete userAttributes.email;
            pendingUserRef.current = cognitoUser;
            pendingAttrsRef.current = userAttributes;
            resolve({ status: 'newPasswordRequired' });
          },
        });
      });
    },
    [applySession],
  );

  const completeNewPassword = useCallback(
    (newPassword: string): Promise<void> => {
      const cognitoUser = pendingUserRef.current;
      if (!cognitoUser) {
        return Promise.reject(new Error('Nenhum desafio de senha pendente.'));
      }
      return new Promise<void>((resolve, reject) => {
        cognitoUser.completeNewPasswordChallenge(newPassword, pendingAttrsRef.current ?? {}, {
          onSuccess: (session) => {
            applySession(cognitoUser, session);
            pendingUserRef.current = null;
            pendingAttrsRef.current = null;
            resolve();
          },
          onFailure: (err) => reject(err),
        });
      });
    },
    [applySession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, completeNewPassword, logout }),
    [user, loading, login, completeNewPassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth deve ser usado dentro de <AuthProvider>.');
  }
  return ctx;
}
